import clsx from 'clsx';
import ErrorText from 'core/modules/atoms/ErrorText';
import { Button, DatePicker, Icon, InputCheckbox, SelectCustomizable, utilsSelectCustomizable } from 'ui';
import InputTimeRange from 'core/modules/atoms/InputTimeRange';
import * as coreEntities from 'core/store/index';
import { weekdays } from 'core/variables/constant';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

// require('moment/locale/es.js');

const PeriodForm = ({ title, t, id, index, onChange, onChangeDays, timetable, applySameShechule, appliedSameShechule, enableAdvancedProgramming }) => {
  const lang = useSelector((state) => coreEntities.i18n.selectors.getLanguage(state));
  const [errorStartDate, setErrorStartDate] = useState(false);
  const [errorEndDate, setErrorEndDate] = useState(false);
  const [minDateFrom, setMinDateFrom] = useState(null);
  const [minDateTo, setMinDateTo] = useState(null);

  const INIT_DAY = {
    guid: new Date().getTime(),
    value: ['08:00', '09:00'],
    day: 'monday',
  };

  const [days, setDays] = useState(timetable ? getCurrentTimeTable().days : [INIT_DAY]);
  let PARSE_WEEKDAYS = utilsSelectCustomizable.getOptionsProperties(weekdays, 'name', 'id');
  PARSE_WEEKDAYS = PARSE_WEEKDAYS.map((item) => {
    const labelKey = typeof item.label === 'string' ? item.label : '';
    return { ...item, label: t(`weekdays:${labelKey}`) };
  });

  useEffect(() => {
    setDays(timetable.find((item) => item.id === id) ? timetable.find((item) => item.id === id).days : []);
    if (timetable[index - 1]?.end_at || timetable[index - 1]?.end_date) {
      const originalDate = new Date(timetable[index - 1]?.end_at ? timetable[index - 1]?.end_at : timetable[index - 1]?.end_date);
      const newDate = new Date(originalDate);
      newDate.setDate(originalDate.getDate() + 1);
      setMinDateFrom(newDate);
    }

    if (getCurrentTimeTable()?.start_at || getCurrentTimeTable()?.start_date) {
      const originalDate = new Date(!enableAdvancedProgramming ? getCurrentTimeTable()?.start_at : getCurrentTimeTable()?.start_date);
      const newDate = new Date(originalDate);
      newDate.setDate(originalDate.getDate() + 1);
      setMinDateTo(newDate);
    }
  }, [timetable]);

  function getIndexOfPeriod() {
    return timetable.findIndex((item) => item.id === id);
  }

  function onNewDay() {
    setDays([...days, INIT_DAY]);
    onChangeDays([...days, INIT_DAY], getIndexOfPeriod());
  }

  function onDeleteDay(dayId) {
    setDays(days.filter((day) => day.guid !== dayId));
    onChangeDays(
      days.filter((day) => day.guid !== dayId),
      getIndexOfPeriod()
    );
  }

  function getCurrentTimeTable() {
    return timetable.find((item) => item.id === id) ? timetable.find((item) => item.id === id) : [INIT_DAY];
  }

  function onChangeDay(e, dayId, attr) {
    let newDays = null;
    if (attr === 'range') {
      if (e.target.validity && e.target.validity.valid === false) {
        newDays = days.map((day) => (day.guid === dayId ? { ...day, error: true } : day));
        setDays(newDays);
      } else {
        newDays = days.map((day) => (day.guid === dayId ? { ...day, error: false, value: e.target.value } : day));
        setDays(newDays);
      }
    } else {
      newDays = days.map((day) => (day.guid === dayId ? { ...day, [attr]: e.value } : day));
      setDays(newDays);
    }
    onChangeDays(newDays, getIndexOfPeriod());
  }

  function onChangeDate(e, attr) {
    if (!e) {
      if (attr === 'start_at' || attr === 'start_date') {
        setErrorStartDate(true);
      } else {
        setErrorEndDate(true);
      }
    } else {
      if (attr === 'start_at' || attr === 'start_date') {
        setErrorStartDate(false);
      } else {
        setErrorEndDate(false);
      }
      onChange(e, attr, getIndexOfPeriod(), true);
    }
  }

  function renderDay(day, i) {
    return (
      <div className="period-form__day" key={i}>
        <div className="period-form__day-week">
          <SelectCustomizable
            label={i === 0 ? t('Day') : <span>&nbsp;</span>}
            options={PARSE_WEEKDAYS}
            placeholder={t('placeholder:Select')}
            onChange={(e) => onChangeDay(e, day.guid, 'day')}
            value={PARSE_WEEKDAYS.find((weekday) => weekday.value === day.day)}
            isSearchable={false}
          />
        </div>
        <div className="period-form__day-schedule">
          <InputTimeRange value={day.value} label={<span>&nbsp;</span>} onChange={(e) => onChangeDay(e, day.guid, 'range')} error={day.error} />
          {day.error && (
            <div className="period-form__day-schedule-error">
              <ErrorText text={t('You must enter a valid time')} />{' '}
            </div>
          )}
        </div>
        <div className="period-form__day-trash" onClick={() => onDeleteDay(day.guid)}>
          <Icon name="trash" />
        </div>
      </div>
    );
  }

  function renderErrorDate() {
    return (
      <div className="period-form__date-error">
        <ErrorText text={t('You must enter a valid date')} />
      </div>
    );
  }

  return (
    <div className="period-form">
      {title && <span className="period-form__title">{title}</span>}
      <div className="period-form__form">
        <div className={clsx('period-form__dates', enableAdvancedProgramming && 'period-form__dates--advanced')}>
          <span className="period-form__subtitle">{t('Dates')}</span>
          <div className="period-form__range-date">
            <DatePicker
              label={t('common:From')}
              onChange={(newStart) => {
                onChangeDate(newStart, !enableAdvancedProgramming ? 'start_at' : 'start_date');
              }}
              value={!enableAdvancedProgramming ? getCurrentTimeTable().start_at : getCurrentTimeTable().start_date}
              shouldDisablePast={false}
              placeholder={!lang.includes('en') || !lang ? 'DD/MM/YYYY' : 'MM/DD/YYYY'}
              minDate={minDateFrom}
              hasYearPicker
            />
            {errorStartDate && renderErrorDate()}
            <div className="period-form__separator-container">
              <div className="period-form__separator"></div>
            </div>
            <DatePicker
              label={t('common:To')}
              onChange={(newFrom) => {
                onChangeDate(newFrom, !enableAdvancedProgramming ? 'end_at' : 'end_date');
              }}
              value={!enableAdvancedProgramming ? getCurrentTimeTable().end_at : getCurrentTimeTable().end_date}
              shouldDisablePast={false}
              placeholder={!lang.includes('en') || !lang ? 'DD/MM/YYYY' : 'MM/DD/YYYY'}
              minDate={minDateTo}
              hasYearPicker
            />
            {errorEndDate && renderErrorDate()}
          </div>
          {getCurrentTimeTable().error && (
            <div className="period-form__day-schedule-error">
              <ErrorText text={t('The course end date must be after the start date')} />{' '}
            </div>
          )}
        </div>
        {!enableAdvancedProgramming && (
          <div className="period-form__schedules">
            <div className="period-form__schedules-header">
              <span className="period-form__subtitle">{t('Shedules')}</span>
              {index === 0 && (
                <div className="period-form__all-period-checkbox">
                  <InputCheckbox label={t('Apply in all periods')} onChange={applySameShechule} checked={appliedSameShechule} />
                </div>
              )}
            </div>

            {days && (
              <div className={`period-form__schedules-form ${days.length === 1 && 'period-form__schedules-form--hide-delete'}`}>
                {days.map((day, i) => renderDay(day, i))}
              </div>
            )}
            <div className="period-form__add-day">
              <Button className="period-form__add-day-button" variant="plain" isIconOnly leftIcon="add-circle" ariaLabel={t('Add new day to schedule')} onClick={onNewDay} />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

PeriodForm.propTypes = {
  title: PropTypes.string,
  t: PropTypes.func,
  index: PropTypes.number,
  onChange: PropTypes.func,
  timetable: PropTypes.array,
  id: PropTypes.string,
  onChangeDays: PropTypes.func,
  applySameShechule: PropTypes.func,
  appliedSameShechule: PropTypes.bool,
  enableAdvancedProgramming: PropTypes.bool,
  course: PropTypes.object,
};

PeriodForm.defaultProps = {
  onChange: function () {},
  onChangeDays: function () {},
  applySameShechule: function () {},
  timetable: [],
  appliedSameShechule: false,
  enableAdvancedProgramming: false,
  course: {},
};

export default PeriodForm;
